import { FC } from "react";
import { connect } from "react-redux";
import { AppDispatch, RootState } from "./store";
import { useNavigate, Routes, Route } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import {
  IPublicClientApplication,
  NavigationClient,
} from "@azure/msal-browser";
import UnifiedReport from "./pages/unifiedReport";
import BuyersReport from "./pages/buyersReport";
import RetailOpsReport from "./pages/retailOpsReport";
import About from "./pages/about";
import UserMonitoring from "./pages/userMonitoring";
import PowerBiReport from "./pages/powerBi";
import AccessControl from "./pages/AccessControl";
import Unauthorized from "./pages/Unauthorized";
import RestrictedRoute from "./routes/RestrictedRoute";
import { UserRole } from "./types/auth.types";
import Alert from "@albertsons/uds/molecule/Alert";
import { ToastMessage } from "./types/toast.types";
import { toggleToastMessage } from "./store/modules/dashboard";

/**
 * This is an example for overriding the default function MSAL uses to navigate to other urls in your webpage
 */
export class CustomNavigationClient extends NavigationClient {
  navigate: any;

  constructor(navigate: any) {
    super();
    this.navigate = navigate;
  }

  /**
   * Navigates to other pages within the same web application
   * You can use the useNavigate hook provided by react-router-dom to take advantage of client-side routing
   * @param url
   * @param options
   */
  async navigateInternal(url: string, options: any) {
    const relativePath = url.replace(window.location.origin, "");
    if (options.noHistory) {
      this.navigate(relativePath, { replace: true });
    } else {
      this.navigate(relativePath);
    }

    return false;
  }
}

interface AppProps {
  pca: IPublicClientApplication;
  toastMessage: ToastMessage;
  toggleToastMessage: (message: ToastMessage) => void;
}

const App: FC<AppProps> = (props) => {
  const { pca, toastMessage, toggleToastMessage } = props;

  const navigate = useNavigate();

  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);

  const onCloseToastMessage = () => {
    toggleToastMessage({ show: false });
  };

  return (
    <MsalProvider instance={pca}>
      <Alert
        isOpen={toastMessage.show}
        sticky={true}
        autoClose={true}
        dismissible={true}
        variant={toastMessage.variant}
        timeout={8}
        className="mr-5 w-[250px]"
        onClose={onCloseToastMessage}
      >
        {toastMessage.message}
      </Alert>
      <Routes>
        <Route
          path="/unified"
          element={
            <RestrictedRoute
              requireAccessToken
              isDetectLocation
              Element={UnifiedReport}
            />
          }
        />
        <Route
          path="/"
          element={
            <RestrictedRoute
              requireAccessToken
              isDetectLocation
              loadFilters={["warehouseDivision", "warehouse", "retailDivision"]}
              Element={BuyersReport}
            />
          }
        />
        <Route
          path="/retailops"
          element={
            <RestrictedRoute
              requireAccessToken
              isDetectLocation
              loadFilters={["warehouseDivision", "warehouse", "retailDivision"]}
              Element={RetailOpsReport}
            />
          }
        />
        <Route
          path="/about"
          element={<RestrictedRoute requireAccessToken Element={About} />}
        />
        <Route
          path="/monitoring"
          element={
            <RestrictedRoute
              requireAccessToken
              role={UserRole.ADMIN}
              Element={UserMonitoring}
            />
          }
        />
        <Route
          path="/power-bi-report"
          element={
            <RestrictedRoute
              requireAccessToken
              role={UserRole.ADMIN}
              Element={PowerBiReport}
            />
          }
        />
        <Route
          path="/access-control"
          element={
            <RestrictedRoute requireAccessToken Element={AccessControl} />
          }
        />
        <Route
          path="/unauthorized"
          element={<RestrictedRoute Element={Unauthorized} />}
        />
      </Routes>
    </MsalProvider>
  );
};

const mapStateToProps = (state: RootState) => ({
  toastMessage: state.dashboard.toastMessage,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  toggleToastMessage: (message: ToastMessage) =>
    dispatch(toggleToastMessage(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
